// GA4 계측 래퍼 (FR-COM-05 / A-4). 기획서 §9 이벤트 규약을 타입으로 고정.
// 원칙: 개인식별정보 0 전송(불변식 1). VITE_GA4_ID 미설정(M1 로컬·정적)이면 no-op +
// dev 콘솔 로그만 → 서버 불필요(M1) 환경에서도 항상 동작/검증 가능.
import { errorStatKey, bookPageStatKey, audioStatKey, type ZoneId } from "@greg-chick/shared";
import { countStat } from "./stat";

// 기획서 §9: view_book_page, play_audio, ar_start, ar_fallback,
//            click_store, enter_world, zone_enter(zone), object_click(id)
export type Ga4Event =
  | { name: "view_book_page"; params: { page: number; total?: number; book?: string } }
  | { name: "play_audio"; params: { page?: number; lang: "ko" | "en"; book?: string } }
  | { name: "ar_start"; params: { scene?: string; mode?: "tracking" | "preview" } }
  | { name: "ar_found"; params: { scene?: string } } // 타깃 첫 인식(FR-AR-07 보조 — 실인식률)
  | { name: "ar_fallback"; params: { scene?: string; reason?: string } }
  | { name: "ar_photo"; params: { action: "open" | "camera" | "capture" | "save" | "share"; scene?: string } }
  | { name: "click_store"; params: { source?: string; item?: string } }
  // 굿즈 상세 열람(docs/build/16) — click_store 와 짝: 얼마나 보고 얼마나 스토어로 갔나
  | { name: "view_goods"; params: { item: string; source?: string } }
  | { name: "enter_world"; params?: { from?: string } }
  /**
   * 🧸 내 친구를 만들어 저장했다. **아이가 이 서비스에 처음 자기 것을 만드는 순간**이라
   * 운영에서 제일 보고 싶은 수 중 하나다. 사람 정보는 없다(만들었다는 사실뿐).
   */
  | { name: "hero_saved"; params?: { first?: boolean } }
  | { name: "zone_enter"; params: { zone: ZoneId } }
  | { name: "object_click"; params: { id: string; type?: string } }
  // 랜딩 진입 버튼 선택(W-01, FR-LAND-02) — 분기 KPI 보조
  | { name: "select_entry"; params: { entry: string } }
  /**
   * 🎉 다 읽은 뒤 무엇을 골랐나 — 「다시 읽기」·「다른 책」·「체험 공간」.
   * 이 나이 아이가 정말 다시 읽는지, 책에서 체험 공간으로 넘어가는지를 본다
   * (책→체험→굿즈로 잇는 게 이 서비스의 목적이다). 책 id 만 보낸다 — 사람 정보는 없다.
   */
  /**
   * 🔴 **끝까지 봤다** — 마지막 쪽 뒤 「다 읽었어요」 화면이 **떴을 때**.
   *
   * 예전에는 그 화면의 **버튼을 눌러야만** 보냈다. 그래서 집계 이름은 `book_done`
   * (「어느 책을 끝까지 봤나」)인데 실제로는 「끝까지 보고 **또 뭔가를 누른** 아이」만 셌다.
   * 그냥 덮고 나가는 아이가 대부분이라 운영에서 **0** 이었다 — 쪽 넘김이 552회인데도.
   * 운영자는 그걸 「아무도 끝까지 안 본다」로 읽고 책을 줄이려 든다(랠리 완주 0 과 같은 함정).
   */
  | { name: "book_end"; params: { book: string } }
  /** 끝내고 **어디로 갔나** — 우리 집계엔 안 넣는다(다음 화면이 말해 준다). */
  | { name: "book_next"; params: { book: string; go: "again" | "more" | "world" } }
  // 설정 환경설정 토글(소리·모션·글자) — 사용성 보조
  | { name: "set_pref"; params: { pref: string; on?: boolean } }
  /**
   * 화면이 깨졌다 — **익명 신호**(lib/errorReport.ts).
   * 자유 문자열은 안 보낸다: 오류 종류·터진 파일·메시지 지문·화면 이름뿐이다.
   * 원문은 브라우저 콘솔에만 남는다(불변식 1 — 서버로 개인식별정보 0).
   */
  | {
      name: "app_error";
      params: {
        kind: string; // TypeError 등
        fp: string; // 메시지 지문(복원 불가) — 같은 오류인지 세는 용도
        source: "boundary" | "window" | "promise";
        where: string; // 미리 정한 화면 이름만
        at?: string; // WorldScene.ts:412
      };
    };

type GtagFn = (
  command: "event" | "config" | "js" | "set",
  targetOrName: string | Date,
  params?: Record<string, unknown>,
) => void;

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: GtagFn;
  }
}

const GA4_ID = import.meta.env.VITE_GA4_ID?.trim();
const isProd = import.meta.env.PROD;
let initialized = false;

/** GA4 스크립트 1회 주입. ID 없으면 아무것도 하지 않음(no-op). */
export function initGa4(): void {
  if (initialized) return;
  initialized = true;

  /**
   * 🔴 **「방문」은 여기서 센다 — GA4 확인보다 먼저.**
   *
   * `visit` 의 뜻은 처음부터 **「첫 화면이 떴다」**였는데(`shared/stats.ts`),
   * 배선은 **체험공간 입장**에 걸려 있었다. 그래서 책만 읽고 간 아이는 **방문에 안 잡혔고**,
   * 관리자 [📊 방문자수]의 「방문」이 실제로는 체험공간 입장 수였다 —
   * **이름과 세는 것이 달랐다**(2026-08-28 발견).
   *
   * 🔴 **GA4 를 안 쓸 때도 세야 한다.** 아래 `GA4_ID` 검사에서 그냥 돌아가므로
   * 반드시 그 **앞**에 둔다 — 지금 운영이 그 상태다(GA4 미설정).
   */
  countStat("visit");

  if (!GA4_ID) {
    if (!isProd) {
      console.info("[ga4] VITE_GA4_ID 미설정 → 계측 no-op (로컬/정적 OK)");
    }
    return;
  }

  window.dataLayer = window.dataLayer || [];
  const gtag: GtagFn = function gtag(...args) {
    window.dataLayer!.push(args);
  };
  window.gtag = gtag;
  gtag("js", new Date());
  // anonymize_ip 등 비식별 옵션. 개인식별정보는 어떤 이벤트에도 싣지 않는다.
  gtag("config", GA4_ID, { anonymize_ip: true, send_page_view: true });

  const s = document.createElement("script");
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(GA4_ID)}`;
  document.head.appendChild(s);
}

/** 타입 안전 이벤트 발화. 규약 외 이름은 컴파일 단계에서 차단된다. */
export function track(event: Ga4Event): void {
  const params = "params" in event ? (event.params ?? {}) : {};
  if (window.gtag && GA4_ID) {
    window.gtag("event", event.name, params as Record<string, unknown>);
  } else if (!isProd) {
    console.debug("[ga4]", event.name, params);
  }
  countOurOwn(event, params as Record<string, unknown>);
}

/**
 * **우리 집계에도 같이 넣는다**(lib/stat.ts).
 *
 * 왜 여기서 하나: 화면마다 `track()` 과 `countStat()` 을 따로 부르게 하면 언젠가 한쪽만
 * 부르게 되고, 그러면 **숫자가 조용히 갈린다.** 부르는 자리를 하나로 둔다.
 *
 * GA4 는 구글 계정이 있어야 켜지고 쿠키를 쓴다. 우리 집계는 계정도 쿠키도 없이 돈다 —
 * 둘 다 있으면 좋지만, **GA4 가 없어도 숫자는 남는다.**
 *
 * 여기 없는 이벤트는 우리 쪽에서 안 센다(예: `set_pref`) — 운영 판단에 안 쓴다.
 *
 * 🔴 **`app_error` 는 2026-08-28 에 여기로 들여왔다.** 그전까지는 GA4 로만 보냈는데
 * GA4 를 안 켠 상태라 **운영에서 통째로 버려지고 있었다** — 조심스럽게 만든 신고가
 * 한 건도 안 남았다. 아이 화면이 하얗게 떠도 아무도 모르는 상태였다.
 */
function countOurOwn(event: Ga4Event, params: Record<string, unknown>): void {
  const str = (v: unknown) =>
    typeof v === "string" || typeof v === "number" ? String(v) : undefined;
  switch (event.name) {
    case "hero_saved":
      countStat("hero_made");
      break;
    case "zone_enter":
      countStat("zone_enter", str(params.zone));
      break;
    case "view_book_page":
      // 🔴 **어느 책의** 몇 쪽인지까지 — 안 그러면 세 책의 쪽 번호가 한 통에 섞여
      //    「어디서 그만 읽나」를 못 답한다(그게 이 숫자를 보는 유일한 이유다)
      countStat("book_page", bookPageStatKey(params.book, params.page));
      break;
    case "view_goods":
      countStat("goods_view", str(params.item));
      break;
    case "book_end":
      // 어느 책을 끝까지 봤나 — 「끝나고 어디로 갔나」는 `book_next` 이고 안 센다
      countStat("book_done", str(params.book));
      break;
    case "enter_world":
      countStat("world_enter", str(params.from));
      break;
    case "select_entry":
      countStat("entry_click", str(params.entry));
      break;
    case "ar_start":
      countStat("ar_open", str(params.scene));
      break;
    case "ar_photo":
      // 열기→찍기→저장/보내기. 어디서 멈추는지가 고칠 곳을 가리킨다
      countStat("ar_photo", str(params.action));
      break;
    case "ar_fallback":
      // 왜 넘어갔는지가 핵심이다 — 권한 거부와 인식 실패는 할 일이 다르다
      countStat("ar_fallback", str(params.reason));
      break;
    case "play_audio":
      // 언어만 세면 「어느 책의 없는 쪽을 채울까」를 못 정한다(stats.ts audioStatKey)
      countStat("audio_play", audioStatKey(params.book, params.lang));
      break;
    case "click_store":
      // 상품을 알면 상품으로, 모르면 어디서 눌렀는지로 — 둘 다 사람 이름이 아니다
      countStat("goods_click", str(params.item) ?? str(params.source));
      break;
    case "app_error":
      /**
       * 🔴 **어느 화면인지만 세면 고칠 수가 없다.** `/rally` 8건을 보고도 새로고침이면
       * 풀리는 것인지 진짜 버그인지 알 수 없었다(2026-08-31 운영 기록). 종류를 같이 센다.
       * 오류 **원문은 여전히 안 보낸다**(불변식 1) — 종류는 클래스 이름이고,
       * `errorKindTag` 가 영문자만 남겨 메시지가 새어 들어올 길을 막는다.
       *
       * 🔴 **터진 자리(`at`)도 여기로 들여왔다**(2026-09-02). 예전엔 만들어 놓고 GA4 로만
       * 보냈는데 GA4 가 꺼져 있어 통째로 버려졌다 — 운영에 `other|TypeError` 2건이
       * 남았는데 어느 코드인지 **끝내 알 수 없었다**. 조각 이름은 우리 것이라 안전하다.
       */
      countStat("app_error", errorStatKey(str(params.where), str(params.kind), str(params.at)));
      break;
    case "object_click":
      countStat("object_open", str(params.id));
      break;
    default:
      break;
  }
}

/** 검사용 — 「한 번만」 상태를 되돌린다. 운영에서는 부르지 않는다. */
export function resetGa4ForTest(): void {
  initialized = false;
}
